import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from 'context/AuthContext';
import { USER_ROLES } from 'services/userService';

/**
 * Roles allowed to reach the admin dashboard.
 *
 * Not a UI preference — it mirrors what the data layer already enforces. The RTDB
 * rules require `auth.token.role == 'admin' || 'manager'` to write
 * menus/siteInfo/siteContent (and even to READ analytics), and verifyAdminAccess
 * in netlify/functions/user-management.js requires the same.
 *
 * Letting anyone else in grants them nothing — the server still says no. It gives
 * them a dashboard that LIES: RTDB applies writes optimistically, so a denied
 * change still renders as though it worked. A staff account could click Force
 * Close, read "Currently force closed", and leave believing online ordering had
 * stopped. It hasn't. That is the defect this guard closes (#457).
 *
 * `user` is deliberately excluded: it is the natural role for customer accounts
 * if #264 ever lands, and customers must never inherit admin surface.
 */
const ADMIN_ROLES = [USER_ROLES.ADMIN, USER_ROLES.MANAGER];

const ProtectedRoute = ({ children, allowedRoles = ADMIN_ROLES }) => {
  const { user, role, loading } = useAuth();

  // `loading` stays true until the ROLE resolves, not just the user (see
  // AuthContext). Redirecting during that gap would bounce a legitimate admin.
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated, but not permitted here. Send them to the storefront rather
  // than /login — they are already signed in, and bouncing them to a login form
  // they have satisfied reads as a bug.
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
};

export default ProtectedRoute;
